import { useEffect, useState } from "react";
import usePreviewLinks from "./utils/preview";
import { useEditor } from "@grapesjs/react";
import { PageTypes } from "@/App";
import { useParams, useSearchParams } from "react-router-dom";
import { useGetPagesQuery } from "@/redux/reducer/pages.api";
import TemplateEditModal from "./modals/TemplateEditModal";
import { identityBadgeHTML, showIdentityBadge } from "./utils/identityBadge";
import { ActionsMenu } from "@talentech/components";
import { ProjectFileCommands } from "@/plugins/import-export";
import { CustomCommands } from "@/shared/utils";
import { useSelector } from "react-redux";
import { getUserSupportStatus } from "@talentech/bff-login";

export default function EditorLoader() {
  const editor = useEditor();
  const isSupport = useSelector(getUserSupportStatus);
  const [showEditModal, setShowEditModal] = useState(false);

  const { type, id } = useParams<{ id: string; type: PageTypes }>();
  const { templates } = useGetPagesQuery(
    {},
    {
      selectFromResult: (res) => ({
        templates:
          type === PageTypes.PAGE
            ? res.data?.page_templates || []
            : res.data?.job_templates || [],
      }),
    }
  );
  usePreviewLinks();

  const currentTemplate = templates.find((template) => template.id === id);
  useEffect(() => {
    if (!currentTemplate) return;

    showIdentityBadge(
      editor.Panels,
      identityBadgeHTML(
        type === PageTypes.PAGE ? "Job listing" : "Job ad",
        currentTemplate.name
      )
    );

    document
      .getElementById("edit-template-name")
      ?.addEventListener("click", () => {
        setShowEditModal(true);
      });
  }, [templates, editor.Panels, currentTemplate, type]);

  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (editor && searchParams.get("revisions") === "true") {
      editor.runCommand(CustomCommands.MODAL_REVISIONS_OPEN, { pageId: id });
    }
  }, [editor, searchParams, id]);

  return (
    <>
      {showEditModal && id && currentTemplate && (
        <TemplateEditModal
          show={showEditModal}
          onClose={() => setShowEditModal(false)}
          templateId={id}
          templateName={currentTemplate.name}
        />
      )}
      <ActionsMenu
        actions={[
          {
            id: "import-project",
            icon: <i className="fa-sharp fa-light fa-file-import" />,
            label: "Import Project",
            onClick: () =>
              editor.runCommand(ProjectFileCommands.IMPORT_PROJECT),
          },
          {
            id: "export-project",
            icon: <i className="fa-sharp fa-light fa-file-export" />,
            label: "Export Project",
            onClick: () =>
              editor.runCommand(ProjectFileCommands.EXPORT_PROJECT),
          },
          {
            id: "custom-project",
            icon: <i className="fa-sharp fa-light fa-book-font" />,
            label: "Google Fonts",
            onClick: () =>
              editor.runCommand(CustomCommands.MODAL_FONT_SELECTOR_OPEN),
          },
          {
            id: "custom-fonts",
            icon: <i className="fa-sharp fa-light fa-font-case" />,
            label: "Custom Fonts",
            onClick: () =>
              editor.runCommand(CustomCommands.MODAL_CUSTOM_FONT_OPEN),
          },
          ...(isSupport
            ? [
                {
                  id: "external-resources",
                  icon: <i className="fa-sharp fa-light fa-paperclip" />,
                  label: "External Resources",
                  onClick: () =>
                    editor.runCommand(
                      CustomCommands.MODAL_EXTERNAL_RESOURCES_OPEN
                    ),
                },
                {
                  id: "custom-code",
                  icon: <i className="fa-sharp fa-light fa-file-code" />,
                  label: "Custom code",
                  onClick: () =>
                    editor.runCommand(CustomCommands.MODAL_CUSTOM_CSS_OPEN),
                },
              ]
            : []),
          {
            id: "revisions",
            icon: <i className="fa-sharp fa-light fa-clock-rotate-left" />,
            label: "Revisions",
            onClick: () =>
              editor.runCommand(CustomCommands.MODAL_REVISIONS_OPEN, {
                pageId: id,
              }),
          },
        ]}
        target={"show-more-options"}
        placement="bottom"
      />
    </>
  );
}
